import { Box, IconButton, Modal } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CLOSE_DIALOGUE } from "../../store/dialogue/dialogue.type";
import {
  IconBuildingBank,
  IconCheck,
  IconCopy,
  IconCreditCard,
  IconFileDescription,
  IconHash,
  IconPhone,
  IconQrcode,
  IconUser,
  IconWallet,
  IconX,
} from "@tabler/icons-react";
import { setToast } from "../../../util/toast";

const invalidCopyValues = new Set(["-", "—"]);

const isValidCopyValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (invalidCopyValues.has(trimmed)) return false;
  }
  return true;
};

const copyToClipboard = async (value) => {
  const text = String(value ?? "").trim();
  if (!text) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // Fall back below.
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textarea);
    return successful;
  } catch (err) {
    return false;
  }
};

const getFieldIcon = (label = "") => {
  const key = String(label).toLowerCase();
  if (key.includes("transaction")) return IconHash;
  if (key.includes("gateway") || key.includes("payment")) return IconWallet;
  if (key.includes("mobile") || key.includes("phone")) return IconPhone;
  if (key.includes("account") || key.includes("card")) return IconCreditCard;
  if (key.includes("upi")) return IconQrcode;
  if (key.includes("bank")) return IconBuildingBank;
  if (key.includes("name") || key.includes("holder")) return IconUser;
  return IconFileDescription;
};

const InfoField = ({ label, value, showCopy = true }) => {
  const [copied, setCopied] = useState(false);
  const Icon = getFieldIcon(label);
  const displayValue =
    value === null || value === undefined || String(value).trim() === ""
      ? "—"
      : String(value).trim();
  const canCopy = showCopy && isValidCopyValue(displayValue);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canCopy) return;
    const success = await copyToClipboard(displayValue);
    if (success) {
      setCopied(true);
      setToast("success", "Copied!");
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="transaction-info-dialog__field">
      <div className="transaction-info-dialog__field-icon" aria-hidden="true">
        <Icon size={18} stroke={1.75} />
      </div>
      <div className="transaction-info-dialog__field-content">
        <label className="transaction-info-dialog__label">{label}</label>
        <div className="transaction-info-dialog__value-box">
          <span className="transaction-info-dialog__value" title={displayValue}>
            {displayValue}
          </span>
          {showCopy && (
            <button
              type="button"
              className="transaction-info-dialog__copy"
              onClick={handleCopy}
              disabled={!canCopy}
              aria-label={`Copy ${label}`}
              title={canCopy ? "Copy" : "Nothing to copy"}
            >
              {copied ? (
                <IconCheck size={16} stroke={2} />
              ) : (
                <IconCopy size={16} stroke={1.75} />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoDialog = () => {
  const { dialogue, dialogueData } = useSelector((state) => state.dialogue);
  const [contactUsDialogue, setContectUsDialogue] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setContectUsDialogue(dialogue);
  }, [dialogue]);

  const handleCloseAddCategory = () => {
    setContectUsDialogue(false);
    dispatch({
      type: CLOSE_DIALOGUE,
      payload: {
        dialogue: false,
      },
    });

    let dialogueData_ = {
      dialogue: false,
    };
    sessionStorage.setItem("dialogueData", JSON.stringify(dialogueData_));
  };

  const paymentDetailFields =
    dialogueData?.paymentDetails
      ?.map((item, index) => {
        const raw = String(item ?? "");
        const separatorIndex = raw.indexOf(":");
        const label =
          separatorIndex === -1
            ? `Detail ${index + 1}`
            : raw.slice(0, separatorIndex).trim() || `Detail ${index + 1}`;
        const value =
          separatorIndex === -1 ? raw.trim() : raw.slice(separatorIndex + 1).trim();
        return { label, value, key: `${label}-${index}` };
      })
      .filter(Boolean) || [];

  return (
    <div>
      <Modal
        open={contactUsDialogue}
        onClose={handleCloseAddCategory}
        aria-labelledby="transaction-info-title"
        aria-describedby="transaction-info-description"
      >
        <Box className="model-style transaction-info-dialog">
          <div className="transaction-info-dialog__header">
            <div className="transaction-info-dialog__header-left">
              <div className="transaction-info-dialog__header-icon" aria-hidden="true">
                <IconFileDescription size={22} stroke={1.75} />
              </div>
              <div>
                <h2 id="transaction-info-title" className="transaction-info-dialog__title">
                  Transaction Info
                </h2>
                <p
                  id="transaction-info-description"
                  className="transaction-info-dialog__subtitle"
                >
                  View transaction details and payment information.
                </p>
              </div>
            </div>
            <IconButton
              aria-label="Close"
              onClick={handleCloseAddCategory}
              className="transaction-info-dialog__close"
              size="small"
            >
              <IconX size={18} stroke={1.75} />
            </IconButton>
          </div>

          <div className="transaction-info-dialog__body">
            <InfoField
              label="Transaction ID"
              value={dialogueData?.uniqueId}
              showCopy
            />
            <InfoField
              label="Payment Gateway"
              value={dialogueData?.paymentGateway}
              showCopy={false}
            />
            {paymentDetailFields.map((field) => (
              <InfoField
                key={field.key}
                label={field.label}
                value={field.value}
                showCopy
              />
            ))}
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default InfoDialog;
